import { Check } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";

const PLANS = [
    {
        id: "developer",
        name: "Developer",
        price: "$0",
        period: "forever",
        quota: "10k events/mo",
        features: ["1 app", "Community support", "7-day event retention"],
    },
    {
        id: "pro",
        name: "Pro",
        price: "$49",
        period: "/mo",
        quota: "1M events/mo",
        features: ["Unlimited apps", "Team members", "90-day retention", "Priority support"],
    },
    {
        id: "scale",
        name: "Scale",
        price: "$249",
        period: "/mo",
        quota: "25M events/mo",
        features: ["Everything in Pro", "SSO & audit log", "1-year retention", "Dedicated infra"],
    },
    {
        id: "enterprise",
        name: "Enterprise",
        price: "Custom",
        period: "",
        quota: "Unlimited",
        features: ["Everything in Scale", "99.99% SLA", "On-prem option", "White-glove onboarding"],
    },
];

export default function BillingTab() {
    const { user } = useAuth();
    const current = user?.plan || "developer";

    const upgrade = (plan) =>
        toast.info("Billing is coming soon", {
            description: `Plan upgrades are handled manually during the beta — email billing@mdh-api.com to move to ${plan.name}.`,
        });

    return (
        <div data-testid="settings-billing-tab">
            <div className="glass-panel flex flex-wrap items-center justify-between gap-3 rounded-xl p-6">
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                        Current plan
                    </div>
                    <div
                        data-testid="billing-current-plan"
                        className="font-display mt-1 text-xl font-bold text-slate-100"
                    >
                        {PLANS.find((p) => p.id === current)?.name || "Developer"}
                    </div>
                </div>
                <div className="font-mono text-[11px] text-slate-500">
                    {PLANS.find((p) => p.id === current)?.quota}
                </div>
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                {PLANS.map((p) => {
                    const isCurrent = p.id === current;
                    return (
                        <div
                            key={p.id}
                            data-testid={`plan-card-${p.id}`}
                            className={`flex flex-col rounded-xl border p-5 ${
                                isCurrent
                                    ? "border-cyan-500/50 bg-cyan-950/20 shadow-[0_0_30px_rgba(6,182,212,0.12)]"
                                    : "border-slate-800/80 bg-[#111827]"
                            }`}
                        >
                            <div className="flex items-center justify-between">
                                <h4 className="font-display text-sm font-bold text-slate-100">
                                    {p.name}
                                </h4>
                                {isCurrent && (
                                    <span className="rounded border border-cyan-500/40 bg-cyan-950/50 px-1.5 py-px font-mono text-[9px] uppercase tracking-wider text-cyan-300">
                                        current
                                    </span>
                                )}
                            </div>
                            <div className="mt-2">
                                <span className="font-display text-2xl font-bold text-slate-100">
                                    {p.price}
                                </span>
                                <span className="font-mono text-[10px] text-slate-500">
                                    {" "}
                                    {p.period}
                                </span>
                            </div>
                            <div className="mt-1 font-mono text-[10px] text-teal-300/80">
                                {p.quota}
                            </div>
                            <ul className="mt-3 flex-1 space-y-1.5">
                                {p.features.map((f) => (
                                    <li
                                        key={f}
                                        className="flex items-start gap-2 text-[11px] text-slate-400"
                                    >
                                        <Check className="mt-0.5 h-3 w-3 shrink-0 text-teal-400" />
                                        {f}
                                    </li>
                                ))}
                            </ul>
                            <button
                                data-testid={`plan-upgrade-${p.id}`}
                                onClick={() => upgrade(p)}
                                disabled={isCurrent}
                                className={`mt-4 rounded-md px-4 py-2 font-mono text-[11px] font-semibold transition-colors ${
                                    isCurrent
                                        ? "cursor-default border border-slate-800 text-slate-600"
                                        : "border border-cyan-500/40 bg-cyan-950/40 text-cyan-300 hover:bg-cyan-950/70"
                                }`}
                            >
                                {isCurrent ? "Your plan" : "Upgrade"}
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
